import { TFunction } from "i18next";
import React from "react";

import ErrorPage from "@is/components/ErrorPage";
import { APP_PATH, LocalStorageKeys, ROUTES } from "@is/constants";
import { LocalStorageService } from "@is/services";
import { sendErrorMessageToSlack } from "@is/utils/slack";

type Props = {
  children: React.ReactNode;
  onError: (errorMessage: string, errorInfo: unknown) => void;
  noReload?: boolean;
  t: TFunction;
};

type State = {
  error: Error;
  directUrl: string;
};

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, directUrl: "" };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError(error.message, {
      ...errorInfo,
      name: error.name,
      message: error.message,
    });

    sendErrorMessageToSlack(this.state.error.message);
  }

  componentDidMount() {
    this.setState({ directUrl: this.getDirectUrl() });
  }

  getDirectUrl() {
    const deviceId = LocalStorageService.getItem(LocalStorageKeys.DeviceId);
    const isEntryView =
      window.location.pathname === APP_PATH ||
      window.location.pathname === `${APP_PATH}/`;
    const isSearchPage = window.location.pathname.includes(ROUTES.ContentView);
    const isConfirmedOrderPage = window.location.pathname.includes(
      ROUTES.ConfirmedOrderView
    );

    if (!deviceId && (isEntryView || isSearchPage || isConfirmedOrderPage)) {
      return `${window.location.origin}/instore${ROUTES.WelcomeView}`;
    }

    return `${window.location.origin}/instore${ROUTES.EntryView}`;
  }

  render() {
    const { error, directUrl } = this.state;
    const { noReload = false, children, t } = this.props;

    if (error) {
      return <ErrorPage t={t} url={directUrl} noReload={noReload} />;
    }

    return children;
  }
}

export default ErrorBoundary;
